import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PawPrint, Quote, Bone } from "lucide-react";
import Reveal from "./Reveal";
import PawTrail from "./PawTrail";
import api from "../lib/api";

const inputCls =
  "w-full rounded-2xl border border-line bg-cream px-5 py-4 text-base text-ink placeholder:text-inkSoft/50 transition-[border-color] duration-200 focus:border-marmalade focus:outline-none";

export const BoneRating = ({ value, size = "h-5 w-5" }) => (
  <div className="flex gap-1" aria-label={`${value} out of 5 bones`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <Bone
        key={n}
        className={`${size} ${n <= value ? "text-marmalade" : "text-line"}`}
        fill={n <= value ? "currentColor" : "none"}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

const ReviewCard = ({ review }) => (
  <div
    className="flex h-full flex-col rounded-3xl border border-line bg-white p-8 shadow-[0_8px_30px_rgba(42,42,42,0.04)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(42,42,42,0.08)]"
    data-testid="review-card"
  >
    <div className="flex items-center justify-between">
      <BoneRating value={review.rating} />
      <Quote className="h-6 w-6 text-sand" fill="currentColor" strokeWidth={0} />
    </div>
    <p className="mt-5 flex-1 font-display text-xl italic leading-relaxed text-ink">“{review.text}”</p>
    <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-inkSoft">
      {review.owner_name} · with {review.pet_name}
    </p>
  </div>
);

const ReviewForm = () => {
  const [form, setForm] = useState({ owner_name: "", pet_name: "", rating: 5, text: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      await api.post("/reviews", form);
      setDone(true);
      toast.success("Thanks! Ari will check it and publish it soon.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not send your review. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center rounded-3xl border border-line bg-white p-10 text-center"
        data-testid="review-form-success"
      >
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-marmalade/10">
          <Bone className="h-7 w-7 text-marmalade" fill="currentColor" strokeWidth={0} />
        </span>
        <h3 className="mt-6 font-display text-3xl font-medium text-ink">Thank you!</h3>
        <p className="mt-3 max-w-xs text-sm leading-relaxed text-inkSoft">
          Your review is on its way to Ari — she'll check it and publish it here soon.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl border border-line bg-white p-8 md:p-10" data-testid="review-form">
      <h3 className="font-display text-3xl font-medium text-ink">Leave a review</h3>
      <p className="mt-2 text-sm leading-relaxed text-inkSoft">Seen by Ari first — published after a quick check.</p>

      <div className="mt-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">How many bones?</span>
        <div className="mt-2 flex gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => setForm((f) => ({ ...f, rating: n }))}
              data-testid={`review-bone-${n}`}
              aria-label={`${n} bones`}
              className="rounded-full p-1 transition-transform duration-200 hover:scale-110"
            >
              <Bone
                className={`h-8 w-8 ${n <= form.rating ? "text-marmalade" : "text-line"}`}
                fill={n <= form.rating ? "currentColor" : "none"}
                strokeWidth={1.5}
              />
            </button>
          ))}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <input
          value={form.owner_name}
          onChange={(e) => setForm((f) => ({ ...f, owner_name: e.target.value }))}
          placeholder="Your name"
          data-testid="review-owner-name"
          className={inputCls}
          required
        />
        <input
          value={form.pet_name}
          onChange={(e) => setForm((f) => ({ ...f, pet_name: e.target.value }))}
          placeholder="Pet's name"
          data-testid="review-pet-name"
          className={inputCls}
          required
        />
      </div>
      <textarea
        value={form.text}
        onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
        rows={4}
        placeholder="How did it go? Ari reads every one."
        data-testid="review-text"
        className={`${inputCls} mt-4 resize-none leading-relaxed`}
        required
      />
      {error && (
        <p className="mt-3 text-xs text-red-600" data-testid="review-form-error">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={sending}
        data-testid="review-submit-btn"
        className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-marmalade py-4 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-marmalade-deep disabled:opacity-60"
      >
        {sending ? "Sending…" : "Send review"}
      </button>
    </form>
  );
};

const ReviewSection = () => {
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    api
      .get("/reviews")
      .then((r) => setReviews(r.data))
      .catch(() => setReviews([]));
  }, []);

  return (
    <section id="reviews" className="border-y border-line bg-sand/50 py-24 md:py-32">
      <div className="mx-auto max-w-[1440px] px-6 md:px-12">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-marmalade">Happy customers</p>
            <h2 className="mt-4 max-w-xl font-display text-4xl font-medium leading-[1.02] tracking-tight text-ink md:text-6xl">
              Happy <em className="text-marmalade">tails</em>, happy humans
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="hidden w-64 md:block">
            <PawTrail count={5} opacityClass="opacity-30" />
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="grid grid-cols-1 content-start gap-6 sm:grid-cols-2 lg:col-span-7">
            {reviews === null && <p className="text-inkSoft">Fetching the happy tails…</p>}
            {reviews?.length === 0 && (
              <p className="text-inkSoft" data-testid="reviews-empty">
                No reviews yet — be the first to leave one!
              </p>
            )}
            {reviews?.map((review, i) => (
              <Reveal key={review.id} delay={(i % 2) * 0.08}>
                <ReviewCard review={review} />
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1} className="lg:col-span-5">
            <ReviewForm />
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default ReviewSection;
